import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import PrintDialog from "../../../components/shared/printTable/PrintDialog";
import { PlanTableFormat } from "../../../types/planning/plan.type";
import DeleteRouteAndTrapAssignmentConfirmDialog from "../inboundPlanning/DeleteRouteAndTrapAssignmentConfirmDialog";
import EditRouteOrTrapDialog from "../inboundPlanning/EditRouteOrTrapDialog";
import type { ShipmentActionTypeId } from "../inboundPlanning/ShipmentActionDialog";
import { AddTrapDialog } from "../prePlanning/tables/traps/AddTrapDialog";
import AddRouteDefaultDialog from "../shared/AddRouteDefaultDialog";

import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { AssignToDialog } from "./AssignToDialog";
import { MergeRoutesDialog } from "./MergeRoutesDialog";
import { MergeRoutesTrapsDialog } from "./MergeRoutesTrapsDialog/MergeRoutesTrapsDialog";
import { MergeTrapsDialog } from "./MergeTrapsDialog";
import { OldAssignToDialog } from "./OldAssignToDialog";

export type SummaryContextType =
  | "bucket"
  | "trap"
  | "route"
  | "section"
  | "shipment";

export type SummaryContext = {
  /** Id of the parent (Route/Trap/Bucket/Section) */
  id: number | string;
  /** Shipment ids of the parent */
  shipmentIds: number[];
  /** Type of the parent `route | trap | bucket | section | shipment` */
  type: SummaryContextType;
};

type ActionDialogName =
  | "create-new-trap"
  | "create-new-route"
  | "assign-to"
  | "merge-routes"
  | "merge-traps"
  | "delete-trap"
  | "delete-route"
  | "delete-plan"
  | "merge-routes-traps"
  | "print"
  | "edit-route-trap";

export interface OpenDialogParams {
  name: Exclude<ActionDialogName, "merge-routes-traps">;
  shipmentContext?: SummaryContext[];
  planSummaries?: PlanTableFormat[];
  onActionSuccess?: (action: ShipmentActionTypeId) => void;
  clearSelection?: () => void;
}

export interface OpenDialogWithMergeRouteParams {
  name: "merge-routes-traps";
  shipmentContext: SummaryContext[];
  planSummaries: PlanTableFormat[];
  clearSelection?: () => void;
  onActionSuccess?: () => void;
}

interface ActionsMenuContextValue {
  activeDialog: ActionDialogName | null;
  openDialog: (
    dialogParams: OpenDialogParams | OpenDialogWithMergeRouteParams
  ) => void;
  closeDialog: () => void;
  shipments: SummaryContext[];
  setShipments: (shipmentContext: SummaryContext[]) => void;
  clearSelection?: () => void;
  planSummaries: PlanTableFormat[];
  isUpdatingClosedRouteOrTrap: (id?: number | string | null) => boolean;
}

const ActionsMenuContext = createContext<ActionsMenuContextValue | null>(null);

type ActionsMenuProviderProps = {
  children: ReactNode;
};

export const ActionsMenuProvider = ({ children }: ActionsMenuProviderProps) => {
  const [activeDialog, setActiveDialog] = useState<ActionDialogName | null>(
    null
  );
  const [shipments, setShipments] = useState<SummaryContext[]>([]);
  const [clearSelection, setClearSelection] = useState<() => void>();
  const [onActionSuccess, setOnActionSuccess] = useState<() => void>();
  const [planSummaries, setPlanSummaries] = useState<PlanTableFormat[]>([]);

  const isOsadFeatureEnabled = useIsFeatureFlagEnabled(
    "inbound-5931-OSAD-assign-form-client"
  );

  const isUpdatingClosedRouteOrTrap = useCallback(
    (planId?: number | string | null) =>
      planSummaries.some(
        (plan) =>
          shipments.some(
            (s) => Number(s.id) === plan.id && !plan.isAvailableForMassage
          ) ||
          (planId && Number(planId) === plan.id && !plan.isAvailableForMassage)
      ),
    [planSummaries, shipments]
  );

  const openDialog = (
    params: OpenDialogParams | OpenDialogWithMergeRouteParams
  ) => {
    params.shipmentContext && setShipments(params.shipmentContext);
    params.clearSelection && setClearSelection(() => params.clearSelection);
    params.onActionSuccess && setOnActionSuccess(() => params.onActionSuccess);
    setPlanSummaries(params.planSummaries ?? []);
    setActiveDialog(params.name);
  };

  function renderActiveDialog() {
    switch (activeDialog) {
      case "create-new-trap":
        return <AddTrapDialog />;
      case "create-new-route":
        return <AddRouteDefaultDialog />;
      case "merge-routes":
        return (
          <MergeRoutesDialog
            clearSelection={clearSelection}
            handleClose={() => setActiveDialog(null)}
            selectedRoutes={shipments}
            routeOrTrap={planSummaries[0]}
          />
        );
      case "merge-traps":
        return (
          <MergeTrapsDialog
            clearSelection={clearSelection}
            handleClose={() => setActiveDialog(null)}
            selectedTraps={shipments}
            routeOrTrap={planSummaries[0]}
          />
        );
      case "merge-routes-traps":
        return (
          <MergeRoutesTrapsDialog
            selectedRouteIds={shipments
              .filter((shipment) => shipment.type === "route")
              .map((shipment) => Number(shipment.id))}
            selectedTrapIds={shipments
              .filter((shipment) => shipment.type === "trap")
              .map((shipment) => Number(shipment.id))}
            onCancel={() => setActiveDialog(null)}
            routeOrTrap={planSummaries[0]}
          />
        );
      case "edit-route-trap":
        return (
          <EditRouteOrTrapDialog
            isOpen={true}
            clearSelection={clearSelection}
            routeOrTrap={planSummaries[0]}
            onSuccess={onActionSuccess}
            onClose={() => setActiveDialog(null)}
          />
        );
      case "print":
        return (
          <PrintDialog
            onCancel={() => setActiveDialog(null)}
            clearSelection={clearSelection}
            planTableRowData={planSummaries}
            setIsPrintDialogOpen={(value) =>
              value ? setActiveDialog("print") : setActiveDialog(null)
            }
          />
        );
      case "delete-plan":
        return (
          <DeleteRouteAndTrapAssignmentConfirmDialog
            open={true}
            plans={planSummaries}
            clearSelection={clearSelection}
            onClose={() => setActiveDialog(null)}
          />
        );
      case "assign-to":
        return isOsadFeatureEnabled ? (
          <AssignToDialog
            source={
              shipments.every((s) => s.type === "route")
                ? "delivery_route"
                : shipments.every((s) => s.type === "trap")
                  ? "delivery_trap"
                  : "osad"
            }
          />
        ) : (
          <OldAssignToDialog
            source={
              shipments.every((s) => s.type === "route") ? "route" : "trap"
            }
          />
        );
      default:
        return null;
    }
  }

  const value = useMemo(
    () => ({
      activeDialog,
      openDialog,
      closeDialog: () => setActiveDialog(null),
      shipments,
      setShipments,
      clearSelection,
      planSummaries,
      isUpdatingClosedRouteOrTrap,
    }),
    [
      activeDialog,
      clearSelection,
      isUpdatingClosedRouteOrTrap,
      planSummaries,
      shipments,
    ]
  );

  return (
    <ActionsMenuContext.Provider value={value}>
      {children}
      {renderActiveDialog()}
    </ActionsMenuContext.Provider>
  );
};

export function useActionsMenuContext(): ActionsMenuContextValue {
  const actionsContext = useContext(ActionsMenuContext);
  if (!actionsContext) {
    throw new Error(
      "useActionsMenuContext must be used within ActionsMenuProvider"
    );
  }
  return actionsContext;
}
